import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

// Shared helpers
const toStr = (v: unknown) => (v === null || v === undefined ? '' : String(v));

/**
 * Chapter name / Video title / Theory / etc validators based on the requirements provided.
 */

/**
 * Allowed chars:
 * Alphabets, Numbers, Spaces, -, ., &
 * Trim leading/trailing spaces
 */
export const courseChapterNameAllowedCharsValidator = (): ValidatorFn => {
  // Allowed: A-Z a-z 0-9 space - . &
  const re = /^[A-Za-z0-9 .&-]+$/;
  return (control: AbstractControl): ValidationErrors | null => {
    const str = toStr(control.value);
    if (!str) return null;
    if (!re.test(str)) return { courseChapterNameAllowedChars: true };
    return null;
  };
};

export const courseChapterNameTrimValidator = (): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const str = toStr(control.value);
    if (!str) return null;
    if (str.trim() !== str) return { courseChapterNameTrim: true };
    return null;
  };
};

export const courseChapterNameNoAllSpacesValidator = (): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const str = toStr(control.value);
    if (str.length > 0 && str.trim().length === 0) {
      return { courseChapterNameNoAllSpaces: true };
    }
    return null;
  };
};

export const courseChapterNameMinLengthValidator = (min: number): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const str = toStr(control.value);
    if (!str) return null;
    const trimmed = str.trim();
    if (trimmed.length > 0 && trimmed.length < min) {
      return { courseChapterNameMinLength: { requiredLength: min, actualLength: trimmed.length } };
    }
    return null;
  };
};

export const courseChapterNameMaxLengthValidator = (max: number): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const str = toStr(control.value);
    if (!str) return null;
    const trimmed = str.trim();
    if (trimmed.length > max) {
      return { courseChapterNameMaxLength: { requiredLength: max, actualLength: trimmed.length } };
    }
    return null;
  };
};

/**
 * Video title validators
 * Allowed:
 * Alphabets, Numbers, Spaces + common punctuation (., ,, -, &, :)
 * Trim extra spaces
 */
export const videoTitleAllowedCharsValidator = (): ValidatorFn => {
  // punctuation includes: . , - & : and spaces
  const re = /^[A-Za-z0-9 .,:&-]+$/;
  return (control: AbstractControl): ValidationErrors | null => {
    const str = toStr(control.value);
    if (!str) return null;
    if (!re.test(str)) return { videoTitleAllowedChars: true };
    return null;
  };
};

export const videoTitleTrimValidator = (): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const str = toStr(control.value);
    if (!str) return null;
    if (str.trim() !== str) return { videoTitleTrim: true };
    return null;
  };
};

export const videoTitleNoAllSpacesValidator = (): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const str = toStr(control.value);
    if (str.length > 0 && str.trim().length === 0) return { videoTitleNoAllSpaces: true };
    return null;
  };
};

export const videoTitleMinLengthValidator = (min: number): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const str = toStr(control.value);
    if (!str) return null;
    const trimmed = str.trim();
    if (trimmed.length > 0 && trimmed.length < min) {
      return { videoTitleMinLength: { requiredLength: min, actualLength: trimmed.length } };
    }
    return null;
  };
};

export const videoTitleMaxLengthValidator = (max: number): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const str = toStr(control.value);
    if (!str) return null;
    const trimmed = str.trim();
    if (trimmed.length > max) {
      return { videoTitleMaxLength: { requiredLength: max, actualLength: trimmed.length } };
    }
    return null;
  };
};

/**
 * Theory content validators
 * - Min 10, Max 5000
 * - Do not allow only spaces
 * - Theory content is required
 */
export const theoryMinLengthValidator = (min: number): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const str = toStr(control.value);
    if (!str) return null;
    if (str.trim().length === 0) return { theoryNoAllSpaces: true };
    const len = str.length;
    if (len > 0 && str.trim().length > 0 && str.trim().length < min) {
      return { theoryMinLength: { requiredLength: min, actualLength: str.trim().length } };
    }
    return null;
  };
};

export const theoryMaxLengthValidator = (max: number): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const str = toStr(control.value);
    if (!str) return null;
    if (str.length > max) return { theoryMaxLength: { requiredLength: max, actualLength: str.length } };
    return null;
  };
};

export const theoryNoAllSpacesValidator = (): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const str = toStr(control.value);
    if (str.length > 0 && str.trim().length === 0) return { theoryNoAllSpaces: true };
    return null;
  };
};

/**
 * Views validators
 * - Only digits
 * - Not negative => digits only ensures
 * - Minimum 0
 */
const ONLY_DIGITS_RE = /^[0-9]+$/;

export const nonNegativeIntegerDigitsValidator = (): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const str = toStr(control.value);
    if (!str) return null;
    if (!ONLY_DIGITS_RE.test(str)) return { nonNegativeIntegerDigits: true };
    const num = Number(str);
    if (!Number.isFinite(num) || num < 0) return { nonNegativeIntegerDigits: true };
    return null;
  };
};

export const nonNegativeIntegerMinValidator = (min: number): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const str = toStr(control.value);
    if (!str) return null;
    if (!ONLY_DIGITS_RE.test(str)) return { nonNegativeIntegerMin: true };
    const num = Number(str);
    if (!Number.isFinite(num) || num < min) return { nonNegativeIntegerMin: { requiredMin: min, actual: num } };
    return null;
  };
};

/**
 * Price validators
 * Same as views (digits only, min 0)
 */
export const priceDigitsNonNegativeValidator = (): ValidatorFn => nonNegativeIntegerDigitsValidator();
export const priceMinValidator = (min: number): ValidatorFn => nonNegativeIntegerMinValidator(min);

/**
 * Video link validator
 * - Must be valid URL (HTTPS links allowed)
 * - Accept YouTube URL, Vimeo URL, MP4 URL
 * - Maximum length 500
 */
export const videoLinkMaxLengthValidator = (max = 500): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const str = toStr(control.value);
    if (!str) return null;
    if (str.length > max) return { videoLinkMaxLength: { requiredLength: max, actualLength: str.length } };
    return null;
  };
};

export const videoLinkValidator = (): ValidatorFn => {
  // Accept: YouTube, Vimeo, and .mp4 links
  // Also enforce it is an http(s) URL
  return (control: AbstractControl): ValidationErrors | null => {
    const str = toStr(control.value);
    if (!str) return null;

    let url: URL;
    try {
      url = new URL(str);
    } catch {
      return { videoLinkInvalidUrl: true };
    }

    if (!['http:', 'https:'].includes(url.protocol)) {
      return { videoLinkInvalidUrl: true };
    }

    const isYouTube = /(^|\.)youtube\.com$/i.test(url.hostname) || /(^|\.)youtu\.be$/i.test(url.hostname);
    const isVimeo = /(^|\.)vimeo\.com$/i.test(url.hostname);
    const isMp4 = url.pathname.toLowerCase().endsWith('.mp4');

    if (!isYouTube && !isVimeo && !isMp4) {
      return { videoLinkInvalidUrl: true };
    }

    return null;
  };
};

